import Box from "@mui/material/Box";
import { MSpinner, MSplitter } from "components/Shared";
import MGrid, { MGridColumnDefinition } from "components/Shared/MGrid";
import React, { useRef, useEffect, useState } from "react";
import { connect} from "react-redux";
import { withRouter } from "tools/routerCompat";
import { getOperationsManagementEmployees } from "requests/employee";
import responseHandler from "responseHandler";
import { userHasRight } from "selectors/rights";
import {
  DATA_TYPE_BOOLEAN,
  DATA_TYPE_NUMBER_UNFORMATTED
} from "typeConstants/dataTypes";
import {
  EMPLOYEE_COMPLAINT_EDIT,
  EMPLOYEE_COMPLAINT_VIEW,
  OPERATIONS_DIRECTOR_PROFILE_EDIT,
  OPERATIONS_MANAGER_PROFILE_EDIT,
  PTO_OPERATIONS_DIRECTOR_VIEW,
  PTO_OPERATIONS_MANAGER_VIEW,
} from "typeConstants/rights";
import OperationsManagementProfile from "./OperationsManagementProfile";
import { EmployeeListDto, EmployeeRoleEnum } from "interfaces/Mueller.Business.Models";
import { clearHeader, updateHeader } from "actions/layout";
import { HeaderDefinition } from "typeConstants/header/headerDefinitions";
import HeaderButton, { HeaderButtonControlMethods } from "components/Shared/HeaderButton/HeaderButton";

interface OperationsManagementListProps {
  //state
  internetUserId: number;
  canEditOpsManagerProfiles: boolean;
  canEditOpsDirectorProfiles: boolean;
  canViewFeedbackCard: boolean;
  canEditFeedbackCard: boolean;
  location: any;
  canViewOpsManagerPto: boolean;
  canViewOpsDirectorPto: boolean;
  canEditOpsManagerPto: boolean;
  canEditOpsDirectorPto: boolean;
  //dispatch
  makeErrorToast: (text: string) => void;
  updateHeader: (definitions: HeaderDefinition) => void;
  clearHeader: () => void;
}

const OperationsManagementList = (props: OperationsManagementListProps) => {

  const mounted = useRef(true);
  const buttonControlMethods = useRef<HeaderButtonControlMethods | null>(null);

  const [employees, setEmployees] = useState<EmployeeListDto[]>([]);
  const [isFetchingEmployees, setIsFetchingEmployees] = useState<boolean>(true);
  const [selectedEmployeeIndex, setSelectedEmployeeIndex] = useState<number>(null);
  const [isIncludingInactiveEmployees, setIsIncludingInactiveEmployees] = useState<boolean>(false);

  const getEmployees = () => {
    getOperationsManagementEmployees(
      props.location?.state?.searchText,
      isIncludingInactiveEmployees,
      (employees: EmployeeListDto[]) => {
        setEmployees(employees);
        setIsFetchingEmployees(false);
      },
      mounted,
      () => {
        props.makeErrorToast("Failed to fetch employees");
      }
    );
  }

  useEffect(() => {
    setEmployees(null);
    setIsFetchingEmployees(true);
    getEmployees();
    buttonControlMethods.current?.setText(isIncludingInactiveEmployees ? "Hide Inactive" : "Show Inactive");
  }, [isIncludingInactiveEmployees]);

  useEffect(() => {
    props.updateHeader({
      buttons: [
        {
          Control: (
            <HeaderButton
              text={isIncludingInactiveEmployees ? "Hide Inactive" : "Show Inactive"}
              onClick={() => {
                setIsIncludingInactiveEmployees(iiie => !iiie);
                setSelectedEmployeeIndex(null);
              }}
              getButtonControlMethods={(cm) => buttonControlMethods.current = cm}
            />)
        }
      ]
    });

    return () => {
      mounted.current = false;
      props.clearHeader();
    };
  }, []);

  if (isFetchingEmployees) {
    return <MSpinner />;
  }

  const columnDefinitions: MGridColumnDefinition[] = [
    {
      key: "supervisor",
      name: "Supervisor",
      dataSource: "supervisorName",
    },
    {
      key: "name",
      name: "Name",
      dataSource: "firstLastName",
    },
    {
      key: "address",
      name: "Address",
      dataSource: "address.address1",
    },
    {
      key: "city",
      name: "City",
      dataSource: "address.city",
    },
    {
      key: "state",
      name: "State",
      dataSource: "address.state",
    },
    {
      key: "zip",
      name: "Zip",
      dataSource: "address.zip",
      dataType: DATA_TYPE_NUMBER_UNFORMATTED,
    },
    {
      key: "extension",
      name: "Extension",
      dataSource: "extension",
      dataType: DATA_TYPE_NUMBER_UNFORMATTED,
    },
    {
      key: "email",
      name: "Email",
      dataSource: "emailAddress",
    },
    {
      key: "active",
      name: "Active",
      dataSource: "isActive",
      dataType: DATA_TYPE_BOOLEAN,
    }
  ];

  const isRenderingSecondChild = selectedEmployeeIndex !== null && selectedEmployeeIndex !== undefined;
  const isViewingOwnProfile = isRenderingSecondChild && employees[selectedEmployeeIndex].internetUserId === props.internetUserId;
  const isViewingOpsManagerProfile = isRenderingSecondChild && [EmployeeRoleEnum.FIELD_OPERATIONS_MANAGER, EmployeeRoleEnum.LENDER_LOGISTICS_MANAGER].includes(employees[selectedEmployeeIndex].roleId);
  const canViewPtoCard = isViewingOwnProfile || (props.canViewOpsManagerPto && isViewingOpsManagerProfile) || (props.canViewOpsDirectorPto);

  const canViewTeamCard =
  //If you can edit ops director profiles, you're allowed to do whatever you want in the profile
  props.canEditOpsDirectorProfiles
  //If you're viewing an ops manager profile, you can view the team card if you can edit ops manager profiles
  || (isViewingOpsManagerProfile && props.canEditOpsManagerProfiles);

  const canViewFeedbackCard =
  //need the right to view
  props.canViewFeedbackCard &&
  //You can always view your own feedback card
  (isViewingOwnProfile
    //If you can edit Ops directors you can do anything
    || props.canEditOpsDirectorProfiles
    //If you're viewing an ops manager profile, you can view the feedback card if you can edit ops manager profiles
    || (isViewingOpsManagerProfile && props.canEditOpsManagerProfiles));

  //If you can view the feedback card you should be able to edit, unless you can only view because you're viewing yourself
  const canAddFeedback = props.canEditFeedbackCard && !isViewingOwnProfile && canViewFeedbackCard;

  return (
    <MSplitter
      firstChild={
        <Box className="padding-page-content" sx={{ height: '100%' }}>
          <MGrid
            columnDefinitions={columnDefinitions}
            data={employees}
            onRowClick={(index) => setSelectedEmployeeIndex(index)}
          />
        </Box>
      }
      secondChildTitle={isRenderingSecondChild &&
      {
        title: `Operations Management - ${employees[selectedEmployeeIndex].firstLastName}`,
        buttons: [
          {
            icon: "close", onClick: () => setSelectedEmployeeIndex(null)
          }
        ]
      }}
      secondChild={isRenderingSecondChild &&
        <OperationsManagementProfile
          employeeId={employees[selectedEmployeeIndex].employeeId}
          internetUserId={employees[selectedEmployeeIndex].internetUserId}
          canViewTeamCard={canViewTeamCard}
          canViewFeedbackCard={canViewFeedbackCard}
          canEditFeedbackCard={canAddFeedback}
          canViewPtoCard={canViewPtoCard}
          employeeRoleId={employees[selectedEmployeeIndex].roleId}
        />
      }
    />
  );
};

const mapStateToProps = state => ({
  internetUserId: state.user.profile.internetUserId,
  canEditOpsManagerProfiles: userHasRight(OPERATIONS_MANAGER_PROFILE_EDIT)(state),
  canEditOpsDirectorProfiles: userHasRight(OPERATIONS_DIRECTOR_PROFILE_EDIT)(state),
  canViewFeedbackCard: userHasRight(EMPLOYEE_COMPLAINT_VIEW)(state),
  canEditFeedbackCard: userHasRight(EMPLOYEE_COMPLAINT_EDIT)(state),
  canViewOpsManagerPto: userHasRight(PTO_OPERATIONS_MANAGER_VIEW)(state),
  canViewOpsDirectorPto: userHasRight(PTO_OPERATIONS_DIRECTOR_VIEW)(state),
});

const mapDispatchToProps = dispatch => ({
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
  updateHeader(headerDefinitions) {
    dispatch(updateHeader(headerDefinitions));
  },
  clearHeader() {
    dispatch(clearHeader());
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OperationsManagementList));